import { useState, useEffect, useCallback } from 'react';

export interface PanelWidthConfig {
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth?: number; // Fixed max width
  minContentWidth?: number; // For dynamic max (windowWidth - minContentWidth)
}

export const usePanelWidth = (config: PanelWidthConfig) => {
  const { storageKey, defaultWidth, minWidth, maxWidth: fixedMaxWidth, minContentWidth } = config;

  const getMaxWidth = useCallback(() => {
    if (fixedMaxWidth !== undefined) {
      return fixedMaxWidth;
    }
    if (minContentWidth !== undefined) {
      return Math.max(minWidth, window.innerWidth - minContentWidth);
    }
    return Infinity;
  }, [fixedMaxWidth, minContentWidth, minWidth]);

  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const savedWidth = localStorage.getItem(storageKey);
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      const max = fixedMaxWidth ?? Infinity;
      return Math.max(minWidth, Math.min(max, width));
    }
    return defaultWidth;
  });

  const updatePanelWidth = useCallback(
    (newWidth: number) => {
      const currentMaxWidth = getMaxWidth();
      const constrainedWidth = Math.max(minWidth, Math.min(currentMaxWidth, newWidth));
      setPanelWidth(constrainedWidth);
      localStorage.setItem(storageKey, constrainedWidth.toString());
    },
    [getMaxWidth, minWidth, storageKey],
  );

  // Listen for storage events (in case multiple tabs are open)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        const width = parseInt(e.newValue, 10);
        if (!isNaN(width)) {
          const max = getMaxWidth();
          setPanelWidth(Math.max(minWidth, Math.min(max, width)));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey, minWidth, getMaxWidth]);

  // Handle window resize for dynamic max width
  useEffect(() => {
    if (fixedMaxWidth !== undefined) {
      return; // No need for resize handler with fixed max
    }

    const handleResize = () => {
      const max = getMaxWidth();
      if (panelWidth > max) {
        updatePanelWidth(max);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [panelWidth, getMaxWidth, updatePanelWidth, fixedMaxWidth]);

  return {
    panelWidth,
    updatePanelWidth,
    minWidth,
    maxWidth: getMaxWidth(),
  };
};
